'use client'

import { Image, PageIndicatorDot } from '@macarongfactory/web-ui'
import { useEffect, useRef, useState } from 'react'

import { type ISeoPlace } from '@/features/seoPlaceDetail/model'

interface IPlacePhotoProps {
  place: ISeoPlace
}

/** 드래그 거리가 사진 한 장 너비의 이 비율만 넘어도 다음/이전 사진으로 전환한다 */
const SWIPE_THRESHOLD_RATIO = 0.15
/** scrollend 미지원 브라우저(구형 Safari 등)에서 스냅 복원을 대신 미루는 시간 */
const SNAP_RESTORE_FALLBACK_MS = 400

const PlacePhoto = ({ place }: IPlacePhotoProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef({ isDragging: false, startX: 0, startScrollLeft: 0 })
  const cancelSnapRestoreRef = useRef<() => void>(() => {})

  const sortedImages = [...(place.images ?? [])].sort(
    (a, b) => a.displayOrder - b.displayOrder
  )
  const photos = Array.from(
    new Map(sortedImages.map(image => [image.url, image])).values()
  )

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!dragRef.current.isDragging || !scrollerRef.current) return
      event.preventDefault()
      const dx = event.clientX - dragRef.current.startX
      scrollerRef.current.scrollLeft = dragRef.current.startScrollLeft - dx
    }
    const handleMouseUp = () => {
      if (!dragRef.current.isDragging) return
      dragRef.current.isDragging = false
      const scroller = scrollerRef.current
      if (!scroller) {
        setIsDragging(false)
        return
      }
      const { startScrollLeft } = dragRef.current
      const slideWidth = scroller.clientWidth
      const startIndex = Math.round(startScrollLeft / slideWidth)
      const delta = scroller.scrollLeft - startScrollLeft
      const threshold = slideWidth * SWIPE_THRESHOLD_RATIO
      const maxIndex = Math.round(scroller.scrollWidth / slideWidth) - 1

      let targetIndex = startIndex
      if (delta > threshold) targetIndex = startIndex + 1
      if (delta < -threshold) targetIndex = startIndex - 1
      targetIndex = Math.min(Math.max(targetIndex, 0), maxIndex)

      scroller.scrollTo({
        left: targetIndex * slideWidth,
        behavior: 'smooth',
      })

      // smooth 스크롤이 끝나기 전에 snap을 복원하면 애니메이션과 겹쳐 목표 위치에서 멈추거나 끊길 수 있어, 스크롤이 끝난 뒤에만 복원한다
      if ('onscrollend' in scroller) {
        const handleScrollEnd = () => setIsDragging(false)
        scroller.addEventListener('scrollend', handleScrollEnd, {
          once: true,
        })
        cancelSnapRestoreRef.current = () =>
          scroller.removeEventListener('scrollend', handleScrollEnd)
      } else {
        const timer = window.setTimeout(
          () => setIsDragging(false),
          SNAP_RESTORE_FALLBACK_MS
        )
        cancelSnapRestoreRef.current = () => window.clearTimeout(timer)
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  if (photos.length === 0) return null

  const handleScroll = () => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const index = Math.round(scroller.scrollLeft / scroller.clientWidth)
    if (index !== activeIndex) setActiveIndex(index)
  }

  const handleMouseDown = (event: React.MouseEvent) => {
    const scroller = scrollerRef.current
    if (!scroller) return
    cancelSnapRestoreRef.current()
    // 직전 스냅 이동(smooth)이 끝나기 전에 다시 드래그를 시작하면 스크롤 위치가 중간값이라
    // 시작 인덱스가 어긋나므로, 진행 중인 스크롤을 현재 위치에서 멈춘다
    scroller.scrollTo({ left: scroller.scrollLeft, behavior: 'auto' })
    dragRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: scroller.scrollLeft,
    }
    setIsDragging(true)
  }

  return (
    <section
      aria-label='정비소 사진'
      className='relative h-[180px] w-full select-none overflow-hidden lg:aspect-[2/1] lg:h-auto'
    >
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        className={`scrollbar-hide flex h-full w-full cursor-grab overflow-x-auto active:cursor-grabbing ${isDragging ? 'snap-none' : 'snap-x snap-mandatory'}`}
      >
        {photos.map((image, index) => (
          <div
            key={image.url}
            className='relative h-full w-full shrink-0 snap-start'
          >
            <Image
              src={image.url}
              alt={`${place.placeName} 이미지 ${index + 1}`}
              width='100%'
              height='100%'
              radius={0}
              scaletype='crop'
              className='absolute inset-0'
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : undefined}
              draggable={false}
            />
          </div>
        ))}
      </div>
      {photos.length > 1 && (
        <div className='pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2'>
          <PageIndicatorDot
            total={photos.length}
            activeIndex={activeIndex}
            variant='white'
          />
        </div>
      )}
    </section>
  )
}

export default PlacePhoto
